import { initReveal } from '../common/reveal';
import { cultureHeaders, getCurrentCulture } from '../common/localizedRoutes';

interface ResourceDto {
    id: string;
    slug: string;
    type: string;
    primaryUrl: string;
    secondaryUrl?: string | null;
    isFeatured: boolean;
    displayOrder: number;
    tags: string[];
    title: string;
    description?: string | null;
    primaryCtaLabel?: string | null;
    secondaryCtaLabel?: string | null;
    createdAt?: string;
    updatedAt?: string;
}

interface ResourceTypeCountDto {
    type: string;
    count: number;
}

interface ResourceTechnologyCountDto {
    tag: string;
    count: number;
}

interface ResourceListResponseDto {
    page: number;
    pageSize: number;
    totalCount: number;
    types: ResourceTypeCountDto[];
    technologies: ResourceTechnologyCountDto[];
    resources: ResourceDto[];
}

type TypeLabels = Record<string, string>;

const ALL_FILTER_KEY = '__all__';
const PAGE_SIZE = 9;

const normalizeUrl = (value: string): string => {
    try {
        return new URL(value, window.location.origin).toString();
    } catch {
        return value;
    }
};

const toTitleCase = (value: string): string =>
    value.replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .split(' ')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ')
    || value;

const getTypeLabels = (source: HTMLElement): TypeLabels => ({
    Repository: source.dataset.typeRepositoryLabel ?? 'Repository',
    Ebook: source.dataset.typeEbookLabel ?? 'Ebook',
    Template: source.dataset.typeTemplateLabel ?? 'Template',
    Guideline: source.dataset.typeGuidelineLabel ?? 'Guideline',
    Examples: source.dataset.typeExamplesLabel ?? 'Examples'
});

const getTypeLabel = (type: string, labels: TypeLabels): string =>
    labels[type] ?? toTitleCase(type);

const getOpenLabel = (source: HTMLElement, resource: ResourceDto): string =>
    resource.primaryCtaLabel ?? source.dataset.openLabel ?? 'Open';

const applySchema = (resources: ResourceDto[]): void => {
    if (!resources.length) {
        return;
    }

    const itemList = resources.map((resource, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
            '@type': 'CreativeWork',
            name: resource.title,
            description: resource.description ?? '',
            url: normalizeUrl(resource.primaryUrl)
        }
    }));

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        itemListElement: itemList
    };

    let script = document.getElementById('resourceSchema') as HTMLScriptElement | null;
    if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.id = 'resourceSchema';
        document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schema);
};

const createResourceCard = (
    resource: ResourceDto,
    labels: TypeLabels,
    openLabelSource: HTMLElement
): HTMLElement => {
    const card = document.createElement('article');
    card.className = 'content-card';
    card.setAttribute('data-reveal', '');
    card.dataset.type = resource.type;
    card.dataset.tags = resource.tags.join(',');

    const link = document.createElement('a');
    link.className = 'content-card__link';
    link.href = resource.primaryUrl;
    link.target = '_blank';
    link.rel = 'noopener';
    link.setAttribute('aria-label', resource.title || resource.slug);

    const badge = document.createElement('span');
    badge.className = 'content-card__type tech-pill';
    badge.textContent = getTypeLabel(resource.type, labels);
    link.appendChild(badge);

    const body = document.createElement('div');
    body.className = 'content-card__body';

    const title = document.createElement('h3');
    title.className = 'content-card__title';
    title.textContent = resource.title || resource.slug;
    body.appendChild(title);

    if (resource.description) {
        const desc = document.createElement('p');
        desc.className = 'content-card__summary';
        desc.textContent = resource.description;
        body.appendChild(desc);
    }

    if (resource.tags.length) {
        const tagList = document.createElement('ul');
        tagList.className = 'content-card__tags';
        resource.tags.slice(0, 3).forEach(tag => {
            const item = document.createElement('li');
            item.className = 'tech-pill';
            item.textContent = tag;
            tagList.appendChild(item);
        });

        const visibleTags = 3;
        if (resource.tags.length > visibleTags) {
            const more = document.createElement('li');
            more.className = 'tech-pill content-card__tag-more';
            more.textContent = `+${resource.tags.length - visibleTags}`;
            tagList.appendChild(more);
        }

        body.appendChild(tagList);
    }

    const action = document.createElement('span');
    action.className = 'btn btn-outline content-card__cta';
    action.textContent = getOpenLabel(openLabelSource, resource);
    body.appendChild(action);

    link.appendChild(body);
    card.appendChild(link);

    return card;
};

const renderFeaturedResources = (
    resources: ResourceDto[],
    container: HTMLElement,
    labels: TypeLabels,
    emptyText: string,
    root: HTMLElement
): void => {
    if (!resources.length) {
        container.innerHTML = `<p class="muted empty-state">${emptyText}</p>`;
        return;
    }

    const fragment = document.createDocumentFragment();
    resources.forEach(resource => {
        fragment.appendChild(createResourceCard(resource, labels, container));
    });

    container.replaceChildren(fragment);
    initReveal({ selector: '#resPage [data-reveal]', root, threshold: 0.18 });
};

const renderResources = (
    resources: ResourceDto[],
    container: HTMLElement,
    labels: TypeLabels,
    emptyText: string,
    root: HTMLElement
): void => {
    if (!resources.length) {
        container.innerHTML = `<p class="muted empty-state">${emptyText}</p>`;
        return;
    }

    const fragment = document.createDocumentFragment();
    resources.forEach(resource => {
        fragment.appendChild(createResourceCard(resource, labels, container));
    });

    container.replaceChildren(fragment);
    initReveal({ selector: '#resPage [data-reveal]', root, threshold: 0.18 });
};

const renderPagination = (
    container: HTMLElement,
    totalCount: number,
    pageSize: number,
    currentPage: number
): void => {
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    if (totalPages <= 1) {
        container.innerHTML = '';
        return;
    }

    const prevLabel = container.getAttribute('data-prev-label') ?? 'Prev';
    const nextLabel = container.getAttribute('data-next-label') ?? 'Next';

    const fragment = document.createDocumentFragment();

    const addButton = (label: string, page: number, disabled = false, active = false): void => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = label;
        button.dataset.page = page.toString();
        if (disabled) {
            button.disabled = true;
        }
        if (active) {
            button.classList.add('is-active');
        }
        fragment.appendChild(button);
    };

    addButton(prevLabel, Math.max(1, currentPage - 1), currentPage <= 1);

    const maxButtons = 5;
    const start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    for (let page = start; page <= end; page += 1) {
        addButton(page.toString(), page, false, page === currentPage);
    }

    addButton(nextLabel, Math.min(totalPages, currentPage + 1), currentPage >= totalPages);

    container.replaceChildren(fragment);
};

const initResourcesSection = (): void => {
    const root = document.getElementById('resPage');
    if (!root) {
        return;
    }

    const cardsWrap = root.querySelector<HTMLElement>('#resCards');
    const featuredWrap = root.querySelector<HTMLElement>('#resFeatured');
    const searchInput = root.querySelector<HTMLInputElement>('#resSearch');
    const technologySelect = root.querySelector<HTMLSelectElement>('#resTechnology');
    const categorySelect = root.querySelector<HTMLSelectElement>('#resCategory');
    const pagination = root.querySelector<HTMLElement>('#resPagination');

    if (!cardsWrap || !featuredWrap || !searchInput || !technologySelect || !categorySelect || !pagination) {
        return;
    }

    const emptyText = cardsWrap.getAttribute('data-empty-text') ?? 'No resources found.';
    const emptyFeatured = featuredWrap.dataset.emptyText ?? emptyText;
    const loadingText = cardsWrap.dataset.loadingText ?? 'Loading...';
    const allTechnologyLabel = technologySelect.dataset.allLabel ?? 'All technologies';
    const allCategoryLabel = categorySelect.dataset.allLabel ?? 'All categories';
    const culture = getCurrentCulture();
    const typeLabels = getTypeLabels(categorySelect);

    let requestToken = 0;
    let filtersInitialized = false;
    let types: { value: string; label: string; count: number }[] = [];
    let technologies: { value: string; label: string; count: number }[] = [];
    let currentTechnology = ALL_FILTER_KEY;
    let currentType = ALL_FILTER_KEY;
    let currentSearch = '';
    let searchTimer = 0;
    let currentPage = 1;

    const setLoading = (): void => {
        cardsWrap.innerHTML = `<p class="muted">${loadingText}</p>`;
    };

    const renderSelectOptions = (
        select: HTMLSelectElement,
        allLabel: string,
        options: { value: string; label: string; count: number }[],
        selectedValue: string
    ): void => {
        const fragment = document.createDocumentFragment();
        const allOption = document.createElement('option');
        allOption.value = ALL_FILTER_KEY;
        allOption.textContent = allLabel;
        fragment.appendChild(allOption);

        options.forEach(({ value, label, count }) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = `${label} (${count})`;
            fragment.appendChild(option);
        });

        select.replaceChildren(fragment);
        select.value = Array.from(select.options).some(option => option.value === selectedValue)
            ? selectedValue
            : ALL_FILTER_KEY;
    };

    const renderFilterSelects = (): void => {
        renderSelectOptions(technologySelect, allTechnologyLabel, technologies, currentTechnology);
        renderSelectOptions(categorySelect, allCategoryLabel, types, currentType);
    };

    const loadFeatured = async (): Promise<void> => {
        featuredWrap.innerHTML = `<p class="muted">${featuredWrap.dataset.loadingText ?? loadingText}</p>`;

        const params = new URLSearchParams();
        params.set('featured', 'true');
        params.set('page', '1');
        params.set('pageSize', '3');

        try {
            const response = await fetch(`/api/catalog/resources?${params.toString()}`, {
                headers: cultureHeaders(culture)
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const data = await response.json() as ResourceListResponseDto;
            renderFeaturedResources(data.resources ?? [], featuredWrap, typeLabels, emptyFeatured, root);
        } catch (error) {
            console.error('Failed to load featured resources', error);
            featuredWrap.innerHTML = `<p class="muted empty-state">${emptyFeatured}</p>`;
        }
    };

    const loadResources = async (): Promise<void> => {
        const token = ++requestToken;
        setLoading();

        const params = new URLSearchParams();
        params.set('page', currentPage.toString());
        params.set('pageSize', PAGE_SIZE.toString());
        if (currentTechnology !== ALL_FILTER_KEY) {
            params.set('tag', currentTechnology);
        }
        if (currentType !== ALL_FILTER_KEY) {
            params.set('type', currentType);
        }
        if (currentSearch) {
            params.set('search', currentSearch);
        }

        try {
            const response = await fetch(`/api/catalog/resources?${params.toString()}`, {
                headers: cultureHeaders(culture)
            });

            if (!response.ok) {
                throw new Error(`Request failed with status ${response.status}`);
            }

            const data = await response.json() as ResourceListResponseDto;
            if (token !== requestToken) {
                return;
            }

            if (!filtersInitialized) {
                types = (data.types ?? [])
                    .map(item => ({
                        value: item.type,
                        label: getTypeLabel(item.type, typeLabels),
                        count: item.count
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

                technologies = (data.technologies ?? [])
                    .map(item => ({
                        value: item.tag,
                        label: item.tag,
                        count: item.count
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

                filtersInitialized = true;
                renderFilterSelects();
            }

            renderResources(data.resources ?? [], cardsWrap, typeLabels, emptyText, root);
            applySchema(data.resources ?? []);
            renderPagination(pagination, data.totalCount, data.pageSize, data.page);
        } catch (error) {
            if (token !== requestToken) {
                return;
            }

            console.error('Failed to load resources', error);
            cardsWrap.innerHTML = `<p class="muted empty-state">${emptyText}</p>`;
            pagination.innerHTML = '';
        }
    };

    searchInput.addEventListener('input', () => {
        window.clearTimeout(searchTimer);
        searchTimer = window.setTimeout(() => {
            currentSearch = searchInput.value.trim();
            currentPage = 1;
            void loadResources();
        }, 250);
    });

    technologySelect.addEventListener('change', () => {
        currentTechnology = technologySelect.value || ALL_FILTER_KEY;
        currentPage = 1;
        void loadResources();
    });

    categorySelect.addEventListener('change', () => {
        currentType = categorySelect.value || ALL_FILTER_KEY;
        currentPage = 1;
        void loadResources();
    });

    pagination.addEventListener('click', event => {
        const target = (event.target as HTMLElement | null)?.closest<HTMLButtonElement>('button[data-page]');
        if (!target || target.disabled) {
            return;
        }

        const pageValue = Number(target.dataset.page ?? '1');
        if (Number.isNaN(pageValue) || pageValue === currentPage) {
            return;
        }

        currentPage = pageValue;
        void loadResources();
    });

    void loadFeatured();
    void loadResources();
};

document.addEventListener('DOMContentLoaded', initResourcesSection);
